import React, { useState } from 'react';
import {
  Button,
  Form,
  Alert,
  Spinner,
  ListGroup,
  Badge,
  ProgressBar,
} from 'react-bootstrap';
import {
  FileExcelFill,
  FiletypeCsv,
  Upload,
  CheckCircle,
  XCircle,
  FileText,
} from 'react-bootstrap-icons';
import { createFromExcel, createFromExcelWithPayloads } from '../../clients/quote_client';
import {
  extractOrderConfirmationCsv,
  submitReviewedOrderConfirmation,
} from '../../clients/order_confirmation_extraction_client';
import { showToast } from '../../config_and_helpers/helpers';
import {
  invalidateSupplierSelectCache,
  invalidateProductSelectCache,
} from '../../config_and_helpers/supplier_cache';
import { parseExcelQuoteFiles } from '../../utils/excelQuoteParser';
import { classifyQuoteSpreadsheetUploadFiles } from '../../utils/quoteSpreadsheetUploadFileTypes';
import { cloneExtractedOrderConfirmationData } from '../../utils/orderConfirmationMapping';
import { validateExtractedOrderConfirmationContext } from '../../utils/orderConfirmationFormatValidation';
import { logOrderConfirmationExtraction } from '../../utils/orderConfirmation/logOrderConfirmationExtraction';
import QuoteUploadReviewStep from './QuoteUploadReviewStep';
import OrderConfirmationExtractionPreview from './orderConfirmation/OrderConfirmationExtractionPreview';
import OrderConfirmationErrorAlert from './orderConfirmation/OrderConfirmationErrorAlert';
import { logInfo, logWarn, logError } from '../../config_and_helpers/logger';
import { PROCURE_SPREADSHEET_UPLOAD_BTN_CLASS } from '../Glass/procureModalUploadButtons';

const LOG_CONTEXT = 'QuoteExcelUploadComponent';
const ORDER_CONFIRMATION_LOG = 'QuoteExcelUploadOrderConfirmation';

/**
 * Unified upload for Excel requisition reports and Sigma order confirmation CSV files.
 *
 * Excel (.xlsx, .xls) → parse in browser → review → bulk create quotes.
 * CSV (.csv) → parse in browser → order confirmation review → multipart submit-reviewed
 * (CSV file + reviewed JSON) → presigned S3 upload → finalize quote upload status.
 *
 * Features:
 * - Multiple file selection with validation (Excel only, max 10MB per file)
 * - Individual file processing to avoid upload size limits
 * - Progress tracking for multi-file uploads
 * - Error handling with user-friendly messages
 * - Display of created quotes after successful processing
 *
 * @param {Object} props - The component props.
 * @param {Function} props.onExcelProcessed - Callback when Excel processing completes.
 * @param {Function} [props.onOrderConfirmationSaved] - Callback when CSV save completes.
 * @param {boolean} props.disabled - Whether the component is disabled (default: false).
 * @param {string} props.token - Authentication token for API requests.
 * @param {Array} [props.supplierOptions] - Materiah suppliers (CSV review).
 * @param {Array} [props.projectOptions]
 * @param {Array} [props.userOptions]
 * @param {*} [props.selectedProject]
 * @param {*} [props.selectedUser]
 * @param {Function} [props.onProjectChange]
 * @param {Function} [props.onUserChange]
 * @returns {JSX.Element} The QuoteExcelUploadComponent.
 */
const QuoteExcelUploadComponent = ({
  onExcelProcessed,
  onOrderConfirmationSaved,
  disabled = false,
  token,
  supplierOptions = [],
  projectOptions = [],
  userOptions = [],
  selectedProject = null,
  selectedUser = null,
  onProjectChange,
  onUserChange,
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  /** @type {'excel'|'order_confirmation'|null} */
  const [uploadMode, setUploadMode] = useState(null);
  const [step, setStep] = useState('files'); // 'files' | 'review' | 'csv-preview' | 'csv-saved'
  const [quotePayloads, setQuotePayloads] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success', 'error', null
  const [errorMessage, setErrorMessage] = useState('');
  const [reviewSubmitError, setReviewSubmitError] = useState('');
  const [createdQuotes, setCreatedQuotes] = useState(null);

  const [extractedData, setExtractedData] = useState(null);
  const [csvExtractError, setCsvExtractError] = useState(null);
  const [csvSaveError, setCsvSaveError] = useState(null);
  const [supplierId, setSupplierId] = useState('');
  const [resolvedFormat, setResolvedFormat] = useState(null);
  const [suggestedSupplier, setSuggestedSupplier] = useState(null);
  const [formatWarnings, setFormatWarnings] = useState([]);

  const resetOrderConfirmationState = () => {
    setExtractedData(null);
    setCsvExtractError(null);
    setCsvSaveError(null);
    setSupplierId('');
    setResolvedFormat(null);
    setSuggestedSupplier(null);
    setFormatWarnings([]);
  };

  const resetAll = () => {
    setSelectedFiles([]);
    setUploadMode(null);
    setStep('files');
    setQuotePayloads([]);
    setUploadStatus(null);
    setErrorMessage('');
    setReviewSubmitError('');
    setCreatedQuotes(null);
    resetOrderConfirmationState();
  };

  const handleFileSelect = event => {
    const files = Array.from(event.target.files || []);
    const classified = classifyQuoteSpreadsheetUploadFiles(files);

    if (classified.errors.length > 0) {
      setErrorMessage(classified.errors.join('\n'));
      setUploadStatus('error');
      setSelectedFiles([]);
      setUploadMode(null);
      return;
    }

    const nextFiles =
      classified.mode === 'order_confirmation' && classified.csvFile
        ? [classified.csvFile]
        : classified.excelFiles;

    setSelectedFiles(nextFiles);
    setUploadMode(classified.mode);
    setStep('files');
    setQuotePayloads([]);
    setUploadStatus(null);
    setErrorMessage('');
    setReviewSubmitError('');
    setCreatedQuotes(null);
    resetOrderConfirmationState();

    logInfo(LOG_CONTEXT, 'files selected', {
      mode: classified.mode,
      fileNames: nextFiles.map(f => f.name),
    });
  };

  const handleOrderConfirmationExtract = async () => {
    const csvFile = selectedFiles[0];
    if (!csvFile || uploadMode !== 'order_confirmation') return;
    setIsUploading(true);
    setCsvExtractError(null);
    setCsvSaveError(null);
    logInfo(ORDER_CONFIRMATION_LOG, 'extract start', { fileName: csvFile.name });
    try {
      const result = await extractOrderConfirmationCsv(csvFile);
      if (result.success && result.data) {
        const draft = cloneExtractedOrderConfirmationData(result.data);
        const formatCtx = validateExtractedOrderConfirmationContext(
          draft,
          supplierOptions
        );

        if (!formatCtx.canProceed) {
          setCsvExtractError({
            message: formatCtx.blockingErrors.join('\n'),
            errorLines: formatCtx.blockingErrors,
            status: null,
          });
          logError(ORDER_CONFIRMATION_LOG, 'extract format gate failed', {
            fileName: csvFile.name,
            errors: formatCtx.blockingErrors,
          });
          return;
        }

        draft.warnings = [...(draft.warnings || []), ...formatCtx.warnings];
        setResolvedFormat(formatCtx.format);
        setSuggestedSupplier(formatCtx.suggestedSupplier);
        setFormatWarnings(formatCtx.warnings);
        if (formatCtx.suggestedSupplier?.value != null) {
          setSupplierId(String(formatCtx.suggestedSupplier.value));
        }

        setExtractedData(draft);
        setStep('csv-preview');

        logOrderConfirmationExtraction(
          ORDER_CONFIRMATION_LOG,
          'extract success — review draft',
          draft,
          {
            fileName: csvFile.name,
            formatId: formatCtx.format?.formatId ?? null,
            suggestedSupplierId: formatCtx.suggestedSupplier?.value ?? null,
          }
        );
      } else {
        setCsvExtractError({
          message:
            result.message ||
            result.error ||
            'Could not extract order confirmation data from this CSV.',
          errorLines: result.errorLines || [],
          status: result.status ?? null,
        });
        logError(ORDER_CONFIRMATION_LOG, 'extract failed', {
          fileName: csvFile.name,
          message: result.message,
        });
      }
    } catch (err) {
      setCsvExtractError({
        message:
          err?.message || 'Could not extract order confirmation data from this CSV.',
        errorLines: [],
        status: null,
      });
      logError(ORDER_CONFIRMATION_LOG, 'extract error', { message: err?.message });
    } finally {
      setIsUploading(false);
    }
  };

  const handleOrderConfirmationSave = async (payload, draft) => {
    if (!token) return;
    const csvFile = selectedFiles[0];
    if (!csvFile) {
      setCsvSaveError({
        message:
          'Order confirmation CSV file is missing. Go back and select the file again.',
        errorLines: [],
        status: null,
      });
      return;
    }
    setIsSubmitting(true);
    setCsvSaveError(null);
    logInfo(ORDER_CONFIRMATION_LOG, 'save start', {
      supplier: payload.supplier,
      orderNumber: payload.order_confirmation?.order_number,
      hasFundingCredit: payload.funding_credit_id != null,
      fileName: csvFile.name,
    });
    try {
      const result = await submitReviewedOrderConfirmation(token, payload, csvFile);
      if (result?.success) {
        invalidateSupplierSelectCache();
        invalidateProductSelectCache();
        setStep('csv-saved');
        if (onOrderConfirmationSaved) {
          onOrderConfirmationSaved({
            success: true,
            quoteId: result.quoteId,
            orderId: result.orderId,
            draft_meta: draft?.meta,
          });
        }
        logInfo(ORDER_CONFIRMATION_LOG, 'save success', {
          quoteId: result.quoteId,
          orderId: result.orderId,
        });
      } else {
        setCsvSaveError({
          message:
            result?.error || result?.message || 'Failed to save quote and order.',
          errorLines: result?.errorLines || [],
          status: result?.status ?? null,
        });
        logError(ORDER_CONFIRMATION_LOG, 'save failed', { message: result?.error });
      }
    } catch (err) {
      setCsvSaveError({
        message: err?.message || 'Failed to save quote and order.',
        errorLines: [],
        status: null,
      });
      logError(ORDER_CONFIRMATION_LOG, 'save error', { message: err?.message });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelCsvPreview = () => {
    logInfo(ORDER_CONFIRMATION_LOG, 'review cancelled');
    setStep('files');
    resetOrderConfirmationState();
  };

  const handleParsePreview = async () => {
    if (uploadMode !== 'excel') {
      return handleOrderConfirmationExtract();
    }
    if (selectedFiles.length === 0) {
      setErrorMessage('Please select at least one Excel file first.');
      setUploadStatus('error');
      return;
    }
    if (!token) {
      setErrorMessage('Authentication token is required');
      setUploadStatus('error');
      return;
    }
    logInfo(LOG_CONTEXT, 'Parse & Preview start', { fileCount: selectedFiles.length, fileNames: selectedFiles.map(f => f.name) });
    setIsUploading(true);
    setErrorMessage('');
    setReviewSubmitError('');
    setUploadStatus(null);
    try {
      const { success, quotePayloads: payloads, errors: parseErrors } = await parseExcelQuoteFiles(selectedFiles);
      if (success && payloads.length > 0) {
        setQuotePayloads(payloads);
        setStep('review');
        logInfo(LOG_CONTEXT, 'Parse & Preview success, showing review', { payloadsCount: payloads.length, parseErrorsCount: parseErrors.length });
        if (parseErrors.length > 0) {
          setErrorMessage(parseErrors.join('\n'));
        }
      } else {
        setUploadStatus('error');
        logWarn(LOG_CONTEXT, 'Parse & Preview no payloads', { parseErrorsCount: parseErrors.length, parseErrors: parseErrors.slice(0, 3) });
        setErrorMessage(
          parseErrors.length > 0
            ? parseErrors.join('\n')
            : 'No quotes could be parsed from the selected files. Check that columns include requisition number and supplier ID (Hebrew or English headers).'
        );
      }
    } catch (err) {
      setUploadStatus('error');
      logError(LOG_CONTEXT, 'Parse & Preview failed', err);
      setErrorMessage(err?.message || 'Failed to parse Excel files.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleConfirmReview = async quotesWithUsers => {
    if (!token) return;
    logInfo(LOG_CONTEXT, 'Create quotes (from review) start', {
      quotesCount: quotesWithUsers?.length ?? 0,
      withProject: (quotesWithUsers || []).filter(q => q.project_id != null).length,
    });
    setIsSubmitting(true);
    setReviewSubmitError('');
    try {
      const result = await createFromExcelWithPayloads(token, selectedFiles, quotesWithUsers);
      const hadCreated = (result.totalCreated ?? 0) > 0;
      if (hadCreated) {
        setUploadStatus('success');
        setCreatedQuotes(result.createdQuotes || []);
        logInfo(LOG_CONTEXT, 'Create quotes (from review) success', { totalCreated: result.totalCreated, errorsCount: result.errors?.length ?? 0 });
        if (result.errors?.length > 0) {
          setErrorMessage(formatErrorMessage(result.errors));
        }
        invalidateSupplierSelectCache();
        invalidateProductSelectCache();
        if (onExcelProcessed) onExcelProcessed(result);
        if (result.toast) result.toast();
        setSelectedFiles([]);
        setStep('files');
        setQuotePayloads([]);
      } else {
        logWarn(LOG_CONTEXT, 'Create quotes (from review) backend errors', { errors: result.errors, message: result.message });
        setReviewSubmitError(
          formatErrorMessage(result.errors || result.message || 'Failed to create quotes.')
        );
      }
    } catch (error) {
      logError(LOG_CONTEXT, 'Create quotes (from review) failed', error);
      setReviewSubmitError(
        error?.message || 'Failed to create quotes. Please try again.'
      );
      showToast(
        error?.message || 'Failed to create quotes.',
        'error',
        'top-right',
        5000
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancelReview = () => {
    logInfo(LOG_CONTEXT, 'Review cancelled, back to file selection');
    setStep('files');
    setQuotePayloads([]);
    setReviewSubmitError('');
  };

  const uploadFilesIndividually = async files => {
    const results = [];

    for (let i = 0; i < files.length; i++) {
      const currentFile = files[i];

      try {
        // Process each file individually
        const result = await createFromExcel(token, [currentFile]);
        results.push(result);

        // If this file failed, continue with others
        // Errors will be displayed in the Alert component
      } catch (error) {
        results.push({
          success: false,
          error: error.message,
          filename: currentFile.name,
        });
      }
    }

    return results;
  };

  const handleUpload = async () => {
    if (uploadMode !== 'excel') return;
    if (selectedFiles.length === 0) {
      setErrorMessage('Please select at least one Excel file first.');
      setUploadStatus('error');
      return;
    }

    setIsUploading(true);
    setUploadStatus(null);
    setErrorMessage('');
    setCreatedQuotes(null);

    try {
      if (!token) {
        throw new Error('Authentication token is required');
      }

      let result;

      // Process files individually to avoid 413 errors
      if (selectedFiles.length > 1) {
        const individualResults = await uploadFilesIndividually(selectedFiles);

        // Combine results from all individual files
        const successfulResults = individualResults.filter(r => r.success);
        const failedResults = individualResults.filter(r => !r.success);

        // Combine all created quotes
        const allCreatedQuotes = successfulResults.flatMap(
          r => r.createdQuotes || []
        );
        const totalCreated = successfulResults.reduce(
          (sum, r) => sum + (r.totalCreated || 0),
          0
        );
        const allErrors = [
          ...successfulResults.flatMap(r => r.errors || []),
          ...failedResults.map(
            r => `${r.filename || 'Unknown file'}: ${r.error || r.message}`
          ),
        ];

        // Errors will be displayed in the Alert component

        const combinedResult = {
          success: successfulResults.length > 0, // Success if at least one file processed
          createdQuotes: allCreatedQuotes,
          totalCreated: totalCreated,
          errors: allErrors,
          message: `Processed ${selectedFiles.length} files individually. ${successfulResults.length} files processed successfully.`,
          toast: () => {
            const message = `Excel processing completed. ${successfulResults.length} of ${selectedFiles.length} files processed successfully. ${totalCreated} quotes created.`;
            showToast(message, 'success', 'top-right', 3000);
          },
        };

        result = combinedResult;
      } else {
        // For single file, upload normally
        result = await createFromExcel(token, selectedFiles);
      }

      const hadCreated = (result.totalCreated ?? 0) > 0;
      if (hadCreated) {
        setUploadStatus('success');
        setCreatedQuotes(result.createdQuotes || []);

        invalidateSupplierSelectCache();
        invalidateProductSelectCache();

        // Display errors/warnings even when some quotes were created (partial success)
        if (result.errors && result.errors.length > 0) {
          setErrorMessage(
            formatErrorMessage(result.errors || 'Failed to process Excel files')
          );
        }

        // Call the callback so parent can refetch quotes list whenever any quote was created
        if (onExcelProcessed) {
          onExcelProcessed(result);
        }

        if (result.toast) {
          result.toast();
        }

        setSelectedFiles([]);
      } else {
        setUploadStatus('error');
        setErrorMessage(
          formatErrorMessage(
            result.errors || result.message || 'Failed to process Excel files'
          )
        );
      }
    } catch (error) {
      setUploadStatus('error');

      // Handle specific 413 error
      if (error.response && error.response.status === 413) {
        const errorMsg =
          'File upload failed: Total file size too large. Please try uploading fewer files at once or contact your administrator to increase the upload limit.';
        setErrorMessage(errorMsg);
        showToast(errorMsg, 'error', 'top-right', 5000);
      } else {
        const errorMsg =
          error.message || 'Failed to upload Excel files. Please try again.';
        setErrorMessage(errorMsg);
        showToast(errorMsg, 'error', 'top-right', 5000);
      }
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = indexToRemove => {
    setSelectedFiles(prev =>
      prev.filter((_, index) => index !== indexToRemove)
    );
    setUploadStatus(null);
    setErrorMessage('');
    setCreatedQuotes(null);
  };

  const handleRemoveAllFiles = () => {
    resetAll();
  };

  if (step === 'csv-saved') {
    return (
      <div className="quote-spreadsheet-upload-component">
        <Alert variant="success" className="mb-0">
          <CheckCircle className="me-2" />
          Quote and order created from order confirmation CSV.
        </Alert>
      </div>
    );
  }

  if (step === 'csv-preview' && extractedData) {
    return (
      <div className="quote-spreadsheet-upload-component">
        <OrderConfirmationExtractionPreview
          extractedData={extractedData}
          token={token}
          resolvedFormat={resolvedFormat}
          suggestedSupplier={suggestedSupplier}
          formatWarnings={formatWarnings}
          supplierOptions={supplierOptions}
          projectOptions={projectOptions}
          userOptions={userOptions}
          selectedProject={selectedProject}
          selectedUser={selectedUser}
          onProjectChange={onProjectChange}
          onUserChange={onUserChange}
          supplierId={supplierId}
          onSupplierChange={setSupplierId}
          onSave={handleOrderConfirmationSave}
          onCancel={handleCancelCsvPreview}
          isSaving={isSubmitting}
          saveError={csvSaveError?.message || ''}
          saveErrorLines={csvSaveError?.errorLines}
          saveErrorStatus={csvSaveError?.status}
          disabled={disabled}
        />
      </div>
    );
  }

  const getTotalFileSize = () => {
    return selectedFiles.reduce((total, file) => total + file.size, 0);
  };

  const formatFileSize = bytes => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Helper function to format error messages
  const formatErrorMessage = errors => {
    if (!errors) {
      return 'Failed to process Excel files';
    }

    // If it's a string, return as is
    if (typeof errors === 'string') {
      if (errors.includes('Supplier with ID') && errors.includes('not found')) {
        return `${errors} Please ensure the supplier's Inner Supplier ID matches the supplier ID in the Excel file. You can set this in the supplier management page.`;
      }
      return errors;
    }

    // If it's an array, join with newlines
    if (Array.isArray(errors)) {
      return errors
        .map(error => {
          const errorStr = String(error);
          if (
            errorStr.includes('Supplier with ID') &&
            errorStr.includes('not found')
          ) {
            return `${errorStr} Please ensure the supplier's Inner Supplier ID matches the supplier ID in the Excel file. You can set this in the supplier management page.`;
          }
          return errorStr;
        })
        .join('\n');
    }

    return 'Failed to process Excel files';
  };

  const isCsvMode = uploadMode === 'order_confirmation';
  const primaryActionLabel = isCsvMode ? 'Extract order data' : 'Parse & Preview';
  const parsingLabel = isCsvMode ? 'Extracting order data…' : 'Parsing Excel…';

  return (
    <div className="quote-spreadsheet-upload-component">
      <div className="quote-spreadsheet-upload-component__head">
        <FileExcelFill size={20} className="text-success" />
        <FiletypeCsv size={20} className="text-success" />
        <h6 className="mb-0 quote-spreadsheet-upload-component__title">
          Excel & order confirmation upload
        </h6>
      </div>

      <div className="quote-file-upload-panel border rounded p-3">
        {csvExtractError && step === 'files' && (
          <OrderConfirmationErrorAlert
            message={csvExtractError.message}
            errorLines={csvExtractError.errorLines}
            status={csvExtractError.status}
            variant="warning"
          />
        )}

        {/* File Selection */}
        <Form.Group className="mb-3">
          <Form.Label className="d-flex align-items-center gap-2">
            <Upload size={16} />
            Select files
          </Form.Label>
          <Form.Control
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            multiple={!isCsvMode}
            onChange={handleFileSelect}
            disabled={disabled || isUploading}
            className="form-control-sm"
          />
          <Form.Text className="text-muted">
            Excel (.xlsx, .xls, max 10MB each): requisition reports — multiple
            files allowed. CSV (.csv, max 5MB): Sigma-Aldrich order confirmation
            — one file at a time. Do not mix Excel and CSV in one selection.
          </Form.Text>
        </Form.Group>

        {/* Selected Files Display */}
        {selectedFiles.length > 0 && step === 'files' && (
          <div className="selected-files-container p-2 bg-white rounded border mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <small className="text-muted">
                {selectedFiles.length} file(s) selected • Total:{' '}
                {formatFileSize(getTotalFileSize())}
              </small>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={handleRemoveAllFiles}
                disabled={isUploading}
                className="btn-sm"
              >
                Remove All
              </Button>
            </div>

            {isUploading && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">{parsingLabel}</small>
                </div>
                <ProgressBar
                  now={100}
                  variant="success"
                  animated
                  className="mb-2"
                />
              </div>
            )}

            <ListGroup variant="flush">
              {selectedFiles.map((file, index) => (
                <ListGroup.Item
                  key={index}
                  className="d-flex align-items-center justify-content-between py-2"
                >
                  <div className="file-info-container d-flex align-items-center gap-2 flex-grow-1 min-width-0">
                    {uploadMode === 'order_confirmation' ? (
                      <FiletypeCsv
                        size={16}
                        className="text-primary flex-shrink-0"
                      />
                    ) : (
                      <FileExcelFill
                        size={16}
                        className="text-success flex-shrink-0"
                      />
                    )}
                    <div className="file-details min-width-0 flex-grow-1">
                      <div
                        className="file-name small text-truncate"
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div className="file-size text-muted small">
                        ({formatFileSize(file.size)})
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveFile(index)}
                    disabled={isUploading}
                    className="flex-shrink-0 ms-2 d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px', padding: '0' }}
                  >
                    <XCircle size={14} />
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}

        {/* Review step: show parsed quotes and user assignment */}
        {step === 'review' && quotePayloads.length > 0 && (
          <div className="selected-files-container p-2 bg-white rounded border mb-3">
            <small className="text-muted d-block mb-2">
              {selectedFiles.length} file(s) • {quotePayloads.length} quote(s) parsed
            </small>
            <QuoteUploadReviewStep
              quotePayloads={quotePayloads}
              token={token}
              projectOptions={projectOptions}
              defaultProjectId={selectedProject}
              onConfirm={handleConfirmReview}
              onCancel={handleCancelReview}
              disabled={disabled}
              confirmLabel="Create quotes"
              isSubmitting={isSubmitting}
              submitError={reviewSubmitError}
            />
          </div>
        )}

        {/* Primary action (file step) */}
        {step === 'files' && (
          <>
            <Button
              variant="secondary"
              onClick={handleParsePreview}
              disabled={selectedFiles.length === 0 || disabled || isUploading}
              className={`w-100 upload-button ${PROCURE_SPREADSHEET_UPLOAD_BTN_CLASS}`}
            >
              {isUploading ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {isCsvMode ? 'Extracting…' : 'Parsing...'}
                </>
              ) : (
                <>
                  <Upload size={16} className="me-2" />
                  {primaryActionLabel}
                </>
              )}
            </Button>
            {selectedFiles.length > 0 && uploadMode === 'excel' && (
              <div className="mt-2 text-center">
                <p className="small text-muted mb-1">
                  Use Parse and Preview to assign project and user per quote before
                  creating.
                </p>
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 text-muted"
                  onClick={handleUpload}
                  disabled={disabled || isUploading}
                >
                  Process without review (skip project and user assignment)
                </Button>
              </div>
            )}
          </>
        )}

        {/* Status Messages */}
        {uploadStatus === 'success' && !errorMessage && (
          <Alert variant="success" className="mt-3 mb-0 py-2">
            <div className="d-flex align-items-center gap-2">
              <CheckCircle size={16} />
              <span className="small">Excel files processed successfully!</span>
            </div>
          </Alert>
        )}

        {errorMessage && (
          <Alert variant="danger" className="mt-3 mb-0 py-2">
            <div className="d-flex align-items-start gap-2">
              <XCircle size={16} className="mt-1 flex-shrink-0" />
              <div className="small" style={{ whiteSpace: 'pre-line' }}>
                {errorMessage}
              </div>
            </div>
          </Alert>
        )}

        {/* Created Quotes Display */}
        {createdQuotes && createdQuotes.length > 0 && (
          <div className="extracted-data-container mt-3">
            <h6 className="d-flex align-items-center gap-2 mb-2">
              <FileText size={16} className="text-success" />
              Created Quotes ({createdQuotes.length})
            </h6>
            <ListGroup>
              {createdQuotes.map((quote, index) => (
                <ListGroup.Item key={index} className="border-0 bg-white">
                  <div className="d-flex align-items-center gap-2 mb-2">
                    <FileExcelFill size={14} className="text-success" />
                    <strong className="small">Quote #{quote.quote_id}</strong>
                  </div>
                  <div className="ms-4">
                    <div className="mb-1">
                      <Badge bg="primary" className="me-2">
                        Requisition Number
                      </Badge>
                      <span className="small">{quote.requisition_number}</span>
                    </div>
                    <div className="mb-1">
                      <Badge bg="info" className="me-2">
                        Supplier
                      </Badge>
                      <span className="small">
                        {quote.supplier}
                        {quote.supplier_id && (
                          <span className="text-muted ms-1">
                            (ID: {quote.supplier_id})
                          </span>
                        )}
                      </span>
                    </div>
                    {quote.supplier_db_id && (
                      <div className="mb-1">
                        <Badge bg="light" text="dark" className="me-2">
                          Supplier DB ID
                        </Badge>
                        <span className="small">{quote.supplier_db_id}</span>
                      </div>
                    )}
                    <div className="mb-1">
                      <Badge bg="secondary" className="me-2">
                        Items Count
                      </Badge>
                      <span className="small">{quote.items_count}</span>
                    </div>
                    <div>
                      <Badge
                        bg={
                          quote.status === 'RECEIVED'
                            ? 'success'
                            : quote.status === 'PENDING'
                            ? 'warning'
                            : 'secondary'
                        }
                        className="me-2"
                      >
                        Status
                      </Badge>
                      <span className="small">{quote.status}</span>
                    </div>
                  </div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}
      </div>
    </div>
  );
};

export default QuoteExcelUploadComponent;
